import {
  formatCurrency,
  transformDateToLocalDateString,
} from "helpers";
import { InvoiceOutgoingRow } from "services/api/fleet/vehicles/invoices/types";
import { ButtonMenu } from "ui/components/Button/ButtonMenu";
import ButtonMenuItem from "ui/components/Button/ButtonMenu/ButtonMenuItem";
import { Column, ColumnTypes } from "ui/components/Table/TableSearch/types";
import Badge from "ui/components/Badge/Badge";
import { getStatusBadgeVariant } from "services/api/offers/consts";
import Tooltip from "ui/components/Tooltip/Tooltip";
import VehicleCell from "ui/components/Table/TableSearch/components/Cells/VehicleCell";
import ContactCell from "ui/components/Table/TableSearch/components/Cells/ContactCell";
import ContractCell from "ui/components/Table/TableSearch/components/Cells/ContractCell";
import OfferCell from "ui/components/Table/TableSearch/components/Cells/OfferCell";

export const INVOICE_SEARCH_COLUMNS = (
  t: (key: string) => string,
  handleVehicleClick: (
    event: React.MouseEvent<HTMLDivElement>,
    id: string
  ) => void,
  handleContactClick: (
    event: React.MouseEvent<HTMLDivElement>,
    id: string
  ) => void,
  handleDeleteClick: (
    event: React.MouseEvent<HTMLSpanElement>,
    id: string
  ) => void
): Column[] => [
    {
      title: "admin.finance.invoice.search.invoicenumber",
      key: "invoice_number",
      field: "invoiceNumber",
      type: ColumnTypes.string,
    },
    {
      title: "admin.finance.invoice.search.contract",
      key: "invoice_contract",
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceOutgoingRow) => {
        return <ContractCell contract={row.contract} />;
      },
    },
    {
      title: "admin.finance.invoice.search.offer",
      key: "invoice_offer",
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceOutgoingRow) => {
        return <OfferCell offer={row.offer} />;
      },
    },
    {
      title: "admin.finance.invoice.search.client.name",
      key: "invoice_client",
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceOutgoingRow) => {
        return (
          <ContactCell
            contact={row.contactClient}
            onClick={(e) => row.contactClient?.id && handleContactClick(e, row.contactClient.id)}
          />
        );
      },
    },
    {
      title: "admin.fleet.vehicle.invoice.search.status",
      key: "invoice_status",
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceOutgoingRow) => {
        if (!row.status) return null;
        return (
          <Badge
            text={row.status.name}
            variant={getStatusBadgeVariant(row.status.color)}
            height="small"
            showDot={true}
          />
        );
      },
    },
    {
      title: "admin.finance.invoice.search.vehicle",
      key: "invoice_vehicle",
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceOutgoingRow) => {
        return (
          <VehicleCell
            vehicle={row.vehicle}
            onClick={(e) => row.vehicle?.id && handleVehicleClick(e, row.vehicle.id)}
          />
        );
      },
    },
    {
      title: "Tip kupca",
      key: "invoice_buyer_type",
      defaultVisible: true,
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceOutgoingRow) => {
        return <div className="text-sm">{row.buyerType?.name ?? "-"}</div>;
      },
    },
    {
      title: "Fisk. status",
      key: "invoice_fiscalization",
      defaultVisible: true,
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceOutgoingRow) => {
        if (!row.fiscalizationStatus) return <div className="text-sm text-slate-400">-</div>;
        const variant = row.fiscalizationStatus === "confirmed" ? "success"
          : row.fiscalizationStatus === "error" ? "danger"
          : "warning";
        return <Badge text={row.fiscalizationStatus} variant={variant} />;
      },
    },
    {
      title: "admin.finance.invoice.search.invoicedate",
      key: "invoice_date",
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceOutgoingRow) => {
        return <div>{transformDateToLocalDateString(row.invoiceDate)}</div>;
      },
    },
    {
      title: "admin.finance.invoice.search.duedate",
      key: "invoice_duedate",
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceOutgoingRow) => {
        const dateStr = transformDateToLocalDateString(row.dueDate);
        if (!row.durationDays) return <>{dateStr}</>;
        return (
          <Tooltip decorate={<span>{dateStr}</span>} position="bottom-left" size="medium">
            <div className="min-w-[200px]">
              <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">Detalji dospijeća</div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-300">Datum dospijeća</span>
                <span className="font-medium">{dateStr}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-300">Broj dana</span>
                <span className="font-bold text-sky-400">{row.durationDays}</span>
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "admin.finance.invoice.search.total",
      key: "invoice_total",
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceOutgoingRow) => (
        <Tooltip
          decorate={<span className="text-md font-medium">{formatCurrency.format(row.total ?? 0)}</span>}
          position="bottom-right"
          size="medium"
        >
          <div className="min-w-[220px]">
            <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">Pregled računa</div>
            {row.buyerType && (
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-300">Tip kupca</span>
                <span className="font-medium">{row.buyerType.name}</span>
              </div>
            )}
            {row.taxRate != null && (
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-300">PDV</span>
                <span className="font-medium">{row.taxRate}%</span>
              </div>
            )}
            <div className="border-t border-slate-600 my-2"></div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-300">Ukupno</span>
              <span className="font-bold text-green-400">{formatCurrency.format(row.total ?? 0)}</span>
            </div>
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Kreirao",
      key: "invoice_created_by",
      defaultVisible: false,
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceOutgoingRow) => {
        return (
          <div className="text-sm text-slate-500">{row.userCreatedName ?? "-"}</div>
        );
      },
    },
    {
      title: "",
      key: "invoice_actions",
      alwaysVisible: true,
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceOutgoingRow) => {
        return (
          <ButtonMenu
            endIcon="/media/icons/more-dots.svg"
            cssClassName="h-full"
            variant="text"
          >
            <ButtonMenuItem
              startIcon="/media/icons/trash.svg"
              variant="remove"
              onClick={(e) => handleDeleteClick(e, row.id)}
            >
              Obriši
            </ButtonMenuItem>
          </ButtonMenu>
        );
      },
    },
  ];
